import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  dynastyPointsFor,
  type OverallStandings,
  type SeasonStandings,
  type StandingsPageData,
} from '@gl/shared';

import { useLeagueLink, useStandings } from '../api/hooks.js';
import { useCurrentUser } from '../auth.js';
import { ErrorState, Loading } from '../components/states.js';
import { Modal } from '../components/Modal.js';
import { HighlightedPositionChart, type PositionSeries } from '../components/PositionChart.js';
import { SeasonSelector } from '../components/SeasonSelector.js';
import { StatsFreshness } from '../components/StatsFreshness.js';
import { OwnTeamMark, ownRowClass } from '../components/ownTeam.js';

const decimal2 = (value: number) => value.toFixed(2);

/**
 * The season on screen, over on Sleeper. Each season is its own league there, so
 * the link follows the selector rather than pointing at the newest one.
 */
function SeasonOnSleeper({ season }: { season: string }) {
  const league = useLeagueLink(season);
  if (!league) return null;

  return (
    <a href={league.url} target="_blank" rel="noreferrer" className="eyebrow hover:text-brass-200">
      {season} on Sleeper <span aria-hidden>↗</span>
    </a>
  );
}

/** Signed, so a climb reads as a climb rather than as a bigger number. */
function Movement({ change }: { change: number | null }) {
  if (change === null) return <span className="text-field-600">—</span>;
  if (change === 0) {
    return (
      <span className="font-display text-xs uppercase tracking-bug text-field-600">unchanged</span>
    );
  }

  const up = change > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-display text-xs font-semibold
        uppercase tracking-bug ${up ? 'bg-brass-400 text-field-950' : 'bg-signal/20 text-signal'}`}
    >
      {up ? '▲' : '▼'} {Math.abs(change)}
    </span>
  );
}

function SeasonTable({ standings, mine }: { standings: SeasonStandings; mine: string | null }) {
  return (
    <div className="table-scroll -mx-4 sm:-mx-5">
      <table className="bx-table">
        <thead>
          <tr>
            <th scope="col" className="w-12 text-right">
              #
            </th>
            <th scope="col" className="text-left">
              Team
            </th>
            <th scope="col" className="text-left">
              Manager
            </th>
            <th scope="col" className="text-right">
              Record
            </th>
            <th scope="col" className="text-right">
              Points for
            </th>
            <th scope="col" className="text-right">
              Points against
            </th>
            {standings.complete ? (
              <>
                <th scope="col" className="text-right">
                  Finished
                </th>
                <th scope="col" className="text-right">
                  Dynasty pts
                </th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {standings.rows.map((row) => (
            <tr
              key={row.id}
              className={`${ownRowClass(row.id === mine)} ${
                row.madePlayoffs ? '' : 'text-field-500'
              }`}
            >
              {/* The rank is the slug down the left, and the playoff cut is a
                  hard volt tick rather than a dot in the middle of it. */}
              <td className="relative py-1 pl-3 pr-3 text-right">
                {row.madePlayoffs ? (
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-1 bg-brass-400"
                    title="Made the playoffs"
                  />
                ) : null}
                <span className={`rank-figure ${row.madePlayoffs ? '' : 'text-field-600'}`}>
                  {row.position}
                </span>
              </td>
              <td className="font-display text-base font-semibold uppercase tracking-tight text-field-50">
                {row.team}
                {row.id === mine ? <OwnTeamMark /> : null}
              </td>
              <td className="text-field-400">{row.manager}</td>
              <td className="text-right font-display text-base font-semibold tabular-nums text-field-100">
                {row.wins}-{row.losses}
                {row.ties ? `-${row.ties}` : ''}
              </td>
              <td className="py-1 text-right">
                <span className="lead-figure">{decimal2(row.pointsFor)}</span>
              </td>
              <td className="text-right tabular-nums text-field-400">
                {decimal2(row.pointsAgainst)}
              </td>
              {standings.complete ? (
                <>
                  <td className="text-right tabular-nums text-field-100">
                    {row.finalPosition ?? '—'}
                  </td>
                  <td className="py-1 text-right">
                    <span className="lead-figure">{row.dynastyPoints ?? '—'}</span>
                  </td>
                </>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 border-l-2 border-field-700 pl-2 text-xs leading-relaxed text-field-500">
        The league plays the median score as a second game each week, so a{' '}
        {standings.regularSeasonWeeks}-week season produces {standings.regularSeasonWeeks * 2}{' '}
        results. A dot marks the top {standings.playoffTeams} who made the playoffs.
        {standings.complete
          ? ' "Finished" is where the playoff and consolation brackets left each team, which is what dynasty points are awarded on.'
          : ' Final placings and dynasty points are awarded once the season ends.'}{' '}
        The volt tick down the left marks a team that made it.
      </p>
    </div>
  );
}

function OverallTable({
  overall,
  latestSeason,
  mine,
}: {
  overall: OverallStandings;
  latestSeason: string;
  mine: string | null;
}) {
  return (
    <div className="table-scroll -mx-4 sm:-mx-5">
      <table className="bx-table">
        <thead>
          <tr>
            <th scope="col" className="w-12 text-right">
              #
            </th>
            <th scope="col" className="text-left">
              Team
            </th>
            <th scope="col" className="text-left">
              Manager
            </th>
            <th scope="col" className="text-right">
              Dynasty pts
            </th>
            <th scope="col" className="text-right">
              After {latestSeason}
            </th>
            <th scope="col" className="text-left">
              By season
            </th>
          </tr>
        </thead>
        <tbody>
          {overall.rows.map((row) => (
            <tr key={row.id} className={ownRowClass(row.id === mine)}>
              <td className="py-1 text-right">
                <span className="rank-figure">{row.position}</span>
              </td>
              <td className="font-display text-base font-semibold uppercase tracking-tight text-field-50">
                {row.team}
                {row.id === mine ? <OwnTeamMark /> : null}
              </td>
              <td className="text-field-400">{row.manager}</td>
              {/* The one number the whole page is about. */}
              <td className="py-1 text-right">
                <span className="font-display text-3xl font-bold leading-none tracking-tight text-brass-400">
                  {row.points}
                </span>
              </td>
              <td className="text-right tabular-nums">
                <Movement change={row.change} />
              </td>
              <td className="text-xs text-field-500">
                {row.seasons
                  .map((season) => `${season.season}: ${season.finalPosition} (+${season.points})`)
                  .join('  ·  ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScoringModal({ numTeams, onClose }: { numTeams: number; onClose: () => void }) {
  const example = [1, 2, 3, numTeams - 1, numTeams];

  return (
    <Modal
      open
      title="How dynasty scoring works"
      onClose={onClose}
      footer={
        <button type="button" className="btn-primary" onClick={onClose}>
          Got it
        </button>
      }
    >
      <div className="space-y-4 text-sm text-field-200">
        <p>
          Every season each team earns points for where it finished — {numTeams} for winning the
          league, down to 1 for finishing last. Overall League Standings is the running total across
          every season the league has played, so it rewards being good year after year rather than
          getting hot once.
        </p>

        <table className="bx-table">
          <thead>
            <tr>
              <th scope="col" className="text-left">
                Finish
              </th>
              <th scope="col" className="text-right">
                Points
              </th>
            </tr>
          </thead>
          <tbody>
            {example.map((position, index) => (
              <tr key={position}>
                <td className="text-field-200">
                  {index === 3 ? '…' : ''}
                  {position === 1
                    ? '1st'
                    : position === 2
                      ? '2nd'
                      : position === 3
                        ? '3rd'
                        : `${position}th`}
                </td>
                <td className="text-right">
                  <span className="lead-figure">{dynastyPointsFor(position, numTeams)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="text-field-400">
          The finish that counts is where the playoff and consolation brackets actually left a team,
          not where the regular season did — so winning the consolation bracket is worth real
          points, and a good regular season that ends badly is not protected.
        </p>
      </div>
    </Modal>
  );
}

export function StandingsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('season') ?? '';
  const { data, isPending, error } = useStandings(requested);
  const [explaining, setExplaining] = useState(false);
  const mine = useCurrentUser()?.sleeperUserId ?? null;

  const page = data as StandingsPageData | undefined;
  const season = page?.season;

  const seasonSeries = useMemo<PositionSeries[]>(() => {
    if (!season) return [];
    return season.rows.map((row) => ({
      id: row.id,
      label: row.team,
      sublabel: row.manager,
      points: season.weekly
        .filter((week) => week.positions[row.id] !== undefined)
        .map((week) => ({ x: week.week, position: week.positions[row.id] as number })),
    }));
  }, [season]);

  const overallSeries = useMemo<PositionSeries[]>(() => {
    if (!page) return [];
    return page.overall.rows.map((row) => ({
      id: row.id,
      label: row.team,
      sublabel: row.manager,
      points: page.overall.bySeason
        .filter((entry) => entry.positions[row.id] !== undefined)
        .map((entry) => ({ x: entry.season, position: entry.positions[row.id] as number })),
    }));
  }, [page]);

  const selectSeason = (next: string) => {
    const updated = new URLSearchParams(params);
    updated.set('season', next);
    setParams(updated, { replace: true });
  };

  return (
    <div>
      <div className="page-head flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <p className="eyebrow mb-1">Dynasty league</p>
          <h1 className="page-title">Standings</h1>
          <p className="mt-2 text-sm text-field-400">
            {season
              ? `${season.season} regular season, ${season.numTeams} teams.`
              : 'Waiting on the first ingestion.'}
          </p>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <StatsFreshness />
          <button type="button" className="btn-secondary" onClick={() => setExplaining(true)}>
            How dynasty scoring works
          </button>
        </div>
      </div>

      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}

      {page && season ? (
        <>
          <SeasonSelector
            seasons={page.seasons}
            active={season.season}
            onSelect={selectSeason}
            aside={<SeasonOnSleeper season={season.season} />}
          />

          <section className="mb-10">
            <h2 className="section-title mb-3">
              {season.complete ? `${season.season} final standings` : `${season.season} standings`}
            </h2>
            <div className="card">
              <SeasonTable standings={season} mine={mine} />
            </div>
          </section>

          <section className="mb-10">
            <h2 className="section-title mb-1">Through the season</h2>
            <p className="mb-3 text-sm text-field-400">
              Where each team stood after every week, and what moved it there — trades, waiver
              pickups and the weeks a team was carrying injuries. Hover a team&apos;s name or its
              line to trace it through the season.
            </p>
            <HighlightedPositionChart
              series={seasonSeries}
              numTeams={season.numTeams}
              mine={mine}
              xLabel="Week"
              empty="No weeks have been played yet."
              events={season.events}
            />
          </section>

          <section className="mb-10">
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="section-title">Overall League Standings</h2>
              <button
                type="button"
                className="eyebrow hover:text-brass-200"
                onClick={() => setExplaining(true)}
              >
                How the points work →
              </button>
            </div>
            <p className="mb-3 text-sm text-field-400">
              Dynasty points from every completed season, with the movement caused by the most
              recent one.
            </p>
            <div className="card">
              {page.overall.rows.length === 0 ? (
                <p className="py-6 text-center text-sm text-field-400">
                  No season has finished yet, so no dynasty points have been awarded.
                </p>
              ) : (
                <OverallTable
                  overall={page.overall}
                  mine={mine}
                  latestSeason={
                    page.overall.bySeason[page.overall.bySeason.length - 1]?.season ?? ''
                  }
                />
              )}
            </div>
          </section>

          {page.overall.bySeason.length > 0 ? (
            <section>
              <h2 className="section-title mb-1">Overall standings over time</h2>
              <p className="mb-3 text-sm text-field-400">
                Where each team sat in the overall table after each season finished.
              </p>
              <HighlightedPositionChart
                series={overallSeries}
                numTeams={season.numTeams}
                mine={mine}
                xLabel="Season"
                empty="No season has finished yet."
              />
            </section>
          ) : null}
        </>
      ) : null}

      {explaining ? (
        <ScoringModal numTeams={season?.numTeams ?? 10} onClose={() => setExplaining(false)} />
      ) : null}
    </div>
  );
}
